import { ArrowForward } from "@mui/icons-material";
import { Box, Button, Link, Paper, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../hooks";
import { getContrastTextColor } from "../utils/colorManager";
import BrandMark from "./brandMark";

export interface JourneyFrameProps {
  serviceName?: string;
  provider?: string;
  logoSrc?: string;
  // Only where a service list exists to return to; a single-service link has
  // none, so it shows neither Change service nor Cancel.
  onLeave?: () => void;
  eyebrow: string;
  title: string;
  description?: string;
  // Translated step names in the journey's own order, and the 0-based
  // position. The template owns the sequence; the frame only draws it.
  stepNames: string[];
  stepIndex: number;
  onBack?: () => void;
  // Omitted on a step whose own result moves the payer on (a search step).
  forward?: {
    label: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  };
  busy?: boolean;
  children: React.ReactNode;
}

// The dummy's hairline, card shadow and title size, shared by the card below.
const line = "divider";
const CARD_SHADOW = "0 1px 2px rgba(16,24,20,.04), 0 8px 24px rgba(16,24,20,.05)";

// Motion is a preference: reduced-motion users get an instant jump.
const scrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    ? "auto"
    : "smooth";

/**
 * The frame every payment journey shares: service header, step title,
 * progress and the footer buttons. Every coloured part reads the resolved
 * theme, so a tenant's primary replaces the default with no change here.
 */
const JourneyFrame = ({
  serviceName,
  provider,
  logoSrc,
  onLeave,
  eyebrow,
  title,
  description,
  stepNames,
  stepIndex,
  onBack,
  forward,
  busy = false,
  children,
}: JourneyFrameProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const primary = theme.color_primary;
  const onPrimary = getContrastTextColor(primary);
  const total = stepNames.length;
  const position = stepIndex + 1;
  const stepOf = t("journey.step_of", { n: position, t: total });
  const cardRef = useRef<HTMLElement>(null);
  const shownStep = useRef(stepIndex);

  // A new step replaces the body, so the payer is taken to its title rather
  // than left mid-page where the previous step's button was.
  useEffect(() => {
    if (shownStep.current === stepIndex) return;
    shownStep.current = stepIndex;
    cardRef.current?.scrollIntoView({ block: "start", behavior: scrollBehavior() });
  }, [stepIndex]);

  return (
    <Paper
      ref={cardRef}
      component="section"
      aria-labelledby="journey-step-title"
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: 760,
        mx: "auto",
        mb: 3,
        minWidth: 0,
        boxSizing: "border-box",
        borderRadius: 4,
        borderColor: line,
        boxShadow: CARD_SHADOW,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: line,
        }}
      >
        <BrandMark
          name={serviceName ?? ""}
          logoUrl={logoSrc}
          size={36}
          radius="10px"
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
            {serviceName}
          </Typography>
          {provider && (
            <Typography variant="body2" color="text.secondary">
              {provider}
            </Typography>
          )}
        </Box>
        {onLeave && (
          <Link
            component="button"
            type="button"
            onClick={onLeave}
            underline="hover"
            sx={{ color: primary, fontWeight: 600, flex: "none" }}
          >
            {t("journey.change_service")}
          </Link>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.75, pb: 0.5, display: "grid", gap: 0.75 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
        >
          {eyebrow}
        </Typography>
        <Typography
          id="journey-step-title"
          variant="h5"
          component="h2"
          fontWeight={700}
          sx={{ fontSize: 22, lineHeight: 1.2, overflowWrap: "anywhere" }}
        >
          {title}
        </Typography>
        <Box
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={position}
          aria-label={stepOf}
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: "1fr",
            gap: 0.5,
            mt: 1,
          }}
        >
          {stepNames.map((name, index) => (
            <Box
              key={name}
              data-testid="journey-progress-segment"
              data-reached={index < position}
              sx={{
                height: 4,
                borderRadius: 1,
                bgcolor: index < position ? primary : line,
              }}
            />
          ))}
        </Box>
        {/* Names on a computer, "Step n of t" on a phone, as in the design. */}
        <Box
          aria-hidden
          sx={{
            display: { xs: "none", sm: "grid" },
            gridAutoFlow: "column",
            gridAutoColumns: "1fr",
            gap: 0.5,
          }}
        >
          {stepNames.map((name, index) => (
            <Typography
              key={name}
              variant="caption"
              color={index === stepIndex ? "text.primary" : "text.secondary"}
              fontWeight={index === stepIndex ? 600 : 400}
            >
              {name}
            </Typography>
          ))}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: "block", sm: "none" } }}
        >
          {stepOf}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.25, pb: 3, minWidth: 0 }}>
        {children}
      </Box>

      {(onLeave || onBack || forward) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.25,
            px: { xs: 2, sm: 3 },
            py: 1.75,
            borderTop: 1,
            borderColor: line,
            bgcolor: "grey.50",
          }}
        >
          {onLeave && (
            <Button
              onClick={onLeave}
              disabled={busy}
              sx={{ color: "text.secondary" }}
            >
              {t("payment.actions.cancel")}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {onBack && (
            <Button
              onClick={onBack}
              disabled={busy}
              variant="outlined"
              sx={{ color: "text.primary", borderColor: line, height: 46, px: 2.5 }}
            >
              {t("payment.actions.back")}
            </Button>
          )}
          {forward && (
            <Button
              onClick={forward.onClick}
              disabled={forward.disabled || busy}
              variant="contained"
              disableElevation
              endIcon={busy ? undefined : <ArrowForward />}
              sx={{
                bgcolor: primary,
                color: onPrimary,
                height: 46,
                px: 2.5,
                borderRadius: 2.5,
                fontWeight: 600,
                "&:hover": { bgcolor: primary },
              }}
            >
              {busy ? t("payment.actions.processing") : forward.label}
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default JourneyFrame;
